import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import Cookies from 'universal-cookie';

import { Calendar } from 'primereact/calendar';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

import constants from 'constants/index';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import GlobalTooltip from 'components/GlobalTooltip';
import ImageComponent from 'shared/ImageComponent';
import useSpinner from 'hooks/useSpinner';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { EditPaymentRecord } from './EditPaymentRecord';
import { listPaymentRecordsV2 } from 'redux/actions/PaymentHistoryAction';
import { deletePaymentRecord } from 'services/billingServices';
import { dollarFormat } from 'utils/utils';
import { RefundPayment } from './RefundPayment';
import { UserDetailsContext } from 'context/userDetailsContext';
import { handlePageNumberInPagination } from 'common/pagination';
import { getPracticeAreaAction } from 'redux/actions/PracticeAreaAction';
import { totalCountEvent } from 'services/generalServices';
import { handleDateTimeOffset, setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';
import { LoaderComponent } from 'shared/LoaderComponent';
import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import EllipsisPagination from 'components/common/EllipsisPagination/EllipsisPagination';

const cookie = new Cookies();
export const PaymentHistory = (props) => {
  const { isFromCaseSpecificModule, handlePaymentHistoryRedirection, isFromGlobalBilling } = props || {};
  const [selectedRecord, setSelectedRecord] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [searchKey, setSearchKey] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const paymentRecords = useSelector((state) => state.paymentHistory.payment_records);

  const userContext = useContext(UserDetailsContext);
  const isLoading = useSelector((state) => state.paymentHistory.isLoading);
  const dispatch = useDispatch();
  const { addToast } = useToast();
  const [spinner, showSpinner, hideSpinner] = useSpinner(true);
  let tenantId = cookie.get('tenantId');
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [pageLimit, setPageLimit] = useState(getPageLimit() ?? 10);
  const [currentPage, setCurrentPage] = useState(1);
  const totalRecords = paymentRecords?.pagination?.total || 0;

  const practiceData = useSelector((state) => state.practiceArea.practiceAreaList['practice-area']);
  const isPracticeAreaLoading = useSelector((state) => state.practiceArea.isLoading);
  const [practiceAreaOptions, setPracticeAreaOptions] = useState([]);

  function getPageLimit() {
    if (props.isLeadSpecific && userContext) {
      return userContext?.firmDetails?.pagination_preference?.leads?.billings?.payment_history;
    } else if (isFromCaseSpecificModule && userContext) {
      return userContext?.firmDetails?.pagination_preference?.case?.billings?.payment_history;
    } else if (userContext) {
      return userContext?.firmDetails?.pagination_preference?.billings?.payment_history;
    }
  }

  function getFilePathForPagination() {
    if (props.isLeadSpecific && userContext) {
      return { module: 'leads', subModule: 'billings', listName: 'payment_history' };
    } else if (isFromCaseSpecificModule && userContext) {
      return { module: 'case', subModule: 'billings', listName: 'payment_history' };
    } else {
      return { module: 'billings', subModule: '', listName: 'payment_history' };
    }
  }

  useEffect(() => {
    if (practiceData && practiceData.length) {
      let practice_options = practiceData.map((val) => {
        return {
          label: val.practice_area_name,
          value: val.practice_area_id,
        };
      });
      setPracticeAreaOptions(practice_options);
    } else {
      setPracticeAreaOptions([]);
    }
  }, [practiceData]);

  useEffect(() => {
    loadPaymentRequests(getPageLimit() ?? 10, 1);
  }, []);

  useEffect(() => {
    if (!isFromCaseSpecificModule) {
      totalCountEvent('practice-area', '', '', '', '', true)
        .then((response) => {
          dispatch(getPracticeAreaAction(response.data, '', 'forward', true));
        })
        .catch((err) => {});
    }
  }, []);

  useEffect(() => {
    if (isLoading) {
      showSpinner();
    } else {
      hideSpinner();
    }
  }, [isLoading]);

  const loadPaymentRequests = (pageLimit = 10, page = 1, filters = {}) => {
    const finalFilters = {
      ...pickExtractedDate(dateFilter),
      search_key: searchKey,
      practice_area: selectedPractice,
      ...filters, // Override with passed filters if any
    };
    if (props.caseView) {
      finalFilters.case_id = props?.caseDetails?.case_id;
    }
    dispatch(listPaymentRecordsV2(finalFilters, pageLimit, page));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    loadPaymentRequests(pageLimit, page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageLimit(newPageSize);
    setCurrentPage(1);
    let path = getFilePathForPagination();
    handlePageNumberInPagination(userContext, path?.module, path?.subModule, path?.listName, newPageSize);
    loadPaymentRequests(newPageSize, 1);
  };
  const onCreateSuccess = () => {
    setShowRecordModal(false);
    loadPaymentRequests(getPageLimit() ?? 10, currentPage);
    typeof props?.getCaseDetails === 'function' && props?.getCaseDetails();
    setShowRefundModal(false);
  };

  const notesBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Notes</span>
        <span className="text-break" title={rowData.description}>
          {rowData.description}
        </span>
      </React.Fragment>
    );
  };

  const paidFromBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid From</span>
        <span className="text-break" title={rowData?.debit_account_name || ''}>
          {rowData?.debit_account_name || '-'}
        </span>
      </React.Fragment>
    );
  };

  const paidToBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Paid To</span>
        <span className="text-break" title={rowData?.deposit_account_name || ''}>
          {rowData?.deposit_account_name || '-'}
        </span>
      </React.Fragment>
    );
  };

  const paymentMethodBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Payment method</span>
        <span className="text-break" title={rowData.payment_method}>
          {rowData.payment_method}
        </span>
      </React.Fragment>
    );
  };

  const amountBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Amount</span>
        <span className="text-break" title={dollarFormat(rowData?.payment_record_amount)}>
          {dollarFormat(rowData?.payment_record_amount)}
        </span>
      </React.Fragment>
    );
  };

  const relatedToBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Related To</span>
        <span className="text-break" title={rowData?.payment_ref_number}>
          {rowData?.payment_ref_number}
        </span>
      </React.Fragment>
    );
  };

  const enterByBody = (rowData) => {
    const firmUserId = rowData?.created_by_details?.user_id;
    let name = rowData?.created_by_details?.first_name;
    if (rowData?.created_by_details?.last_name) name = name + ' ' + rowData?.created_by_details?.last_name;

    return (
      <>
        <span className="p-column-title text-break">Entered By</span>
        <Link
          to={firmUserId ? `/settings/team-management/${firmUserId}` : '/settings/team-management'}
          className="redirection-link-no-text-transform"
        >
          <div className="d-flex align-items-center">
            <ImageComponent
              filePath={`${tenantId}/${firmUserId}/profile_image`}
              fileName={rowData?.created_by_details?.first_name}
              fileSize="small.jpg"
            />
            <span
              className="redirection-link-no-text-transform"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '150px',
              }}
              title={name}
            >
              {firmUserId && <i className="fas fa-user-tie me-2"></i>}
              {name}
            </span>
          </div>
        </Link>
      </>
    );
  };

  const dateBody = (rowData) => {
    return (
      <React.Fragment>
        <span className="p-column-title text-break">Date</span>
        <span
          className="text-break"
          title={handleDateTimeOffset(userContext?.userDetails?.timezone, rowData.payment_record_date, constants.month_date_year_format)}
        >
          {handleDateTimeOffset(userContext?.userDetails?.timezone, rowData.payment_record_date, constants.month_date_year_format)}
        </span>
      </React.Fragment>
    );
  };

  const caseBody = (rowData, ...rest) => {
    const { case_id, is_lead } = rowData;
    const mainModuleName = is_lead ? '/leads' : '/cases';
    let route = case_id ? `${mainModuleName}/${case_id}` : mainModuleName;

    return (
      <React.Fragment>
        <span className="p-column-title text-break">Case</span>
        <Link to={route} className="redirection-link-no-text-transform" title={rowData.case_name ? rowData.case_name : undefined}>
          {rowData.case_name ? rowData.case_name : '-'}
        </Link>
      </React.Fragment>
    );
  };

  const actionBody = (rowData) => {
    return (
      <>
        <span className="p-column-title text-break">Action</span>

        {!rowData.is_refund &&
          (!rowData?.fund_transfer_record ? rowData.payment_record_ref === '' : true) &&
          rowData.payment_record_type !== 'REFUND' &&
          rowData?.is_forwarded !== true &&
          !rowData?.is_transferred && (
            <GlobalTooltip
              className="ms-2"
              tooltip={rowData?.qb_charge_id ? "Payment made through payment gateway(client portal) can't be edited." : 'Edit'}
            >
              <span role="button">
                <i
                  className={`icon-edit ${(rowData?.qb_charge_id || parseFloat(rowData?.due_amount) <= 0) && 'disabled-icon'}`}
                  onClick={(e) => {
                    setSelectedRecord({ ...rowData });
                    setShowRecordModal(true);
                  }}
                  id="edit"
                ></i>
              </span>
            </GlobalTooltip>
          )}

        {(!rowData.is_refund || (rowData.is_refund === true && rowData.payment_record_type === 'REFUND')) && !rowData?.is_transferred && (
          <GlobalTooltip className="ms-2" tooltip={'Delete'}>
            <span role="button">
              <i
                className={`icon-delete`}
                onClick={(e) => {
                  setSelectedRecord({ ...rowData });
                  setConfirmDelete(true);
                }}
                id="delete"
              ></i>
            </span>
          </GlobalTooltip>
        )}
        {rowData.is_refund === false &&
          rowData.payment_record_ref === '' &&
          rowData.payment_record_type !== 'REFUND' &&
          !rowData?.fund_transfer_record && (
            <span
              className={
                'ms-2 redirection-link' +
                (rowData?.qb_charge_id && !userContext?.userDetails?.is_quickbook_connected ? 'pe-none opacity-50 disabled-icon' : '')
              }
              role="button"
              onClick={() => {
                setSelectedRecord({ ...rowData });
                setShowRefundModal(true);
              }}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Refund
            </span>
          )}
      </>
    );
  };

  const deleteRequest = () => {
    if (selectedRecord) {
      setConfirmDelete(false);
      deletePaymentRecord(selectedRecord.sk, props.isLeadSpecific ? props?.isLeadSpecific : selectedRecord?.is_lead)
        .then(() => {
          addToast(
            false,
            toastConstant.toasterType.SUCCESS,
            toastConstant.api.SUCCESS,
            toastConstant.message.DELETE_PAYMENT_RECORD_SUCCESS
          );
          loadPaymentRequests(getPageLimit() ?? 10, currentPage);
        })
        .catch((err) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.DELETE_PAYMENT_RECORD_FAILED);
        });
    }
  };

  const onSearchKeyDown = (e) => {
    let code = e.keyCode ? e.keyCode : e.which;
    if (code === 13) {
      const searchValue = e.target.value;
      setCurrentPage(1);
      loadPaymentRequests(getPageLimit() ?? 10, 1, {
        search_key: searchValue,
        practice_area: selectedPractice,
      });
    }
  };

  const pickExtractedDate = (dateArray) => {
    return {
      payment_date_lower: dateArray?.[0]
        ? setTimezoneWithoutAlteringTime(moment(dateArray[0]).startOf('day'), userContext?.userDetails.timezone)
        : '',
      payment_date_upper: dateArray?.[0]
        ? setTimezoneWithoutAlteringTime(moment(dateArray[1] ? dateArray[1] : dateArray[0]).endOf('day'), userContext?.userDetails.timezone)
        : '',
    };
  };

  const onChangeSearch = (e) => {
    let value = e.target.value;
    setSearchKey(value);
    if (!value) {
      setCurrentPage(1);
      loadPaymentRequests(getPageLimit() ?? 10, 1, {
        search_key: '',
        practice_area: selectedPractice,
      });
    }
  };

  const onChangeCalendar = (e) => {
    setDateFilter(e.value);
    setCurrentPage(1);
    loadPaymentRequests(getPageLimit() ?? 10, 1, {
      ...pickExtractedDate(e.value),
      practice_area: selectedPractice, // Keep current practice area
    });
  };
  const onChangePractice = (e) => {
    const newPracticeValue = [...e.value];
    setSelectedPractice(newPracticeValue);
    setCurrentPage(1);
    loadPaymentRequests(getPageLimit() ?? 10, 1, { practice_area: newPracticeValue });
  };

  const refreshCase = () => {
    setSearchKey('');
    setDateFilter('');
    setSelectedPractice();
    setCurrentPage(1);
    loadPaymentRequests(getPageLimit() ?? 10, 1, {
      search_key: '',
      practice_area: '',
      payment_date_upper: '',
      payment_date_lower: '',
    });
  };

  return (
    <div>
      {spinner}
      <ConfirmDialog
        header="Confirm Delete"
        rejectClassName="p-button-secondary outline"
        message={
          <span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-exclamation-triangle"></i>
              Are you sure you want to delete this transaction? This cannot be undone.
            </span>
            <br />
            <br />
            Deleting the transaction won’t refund your customer. The transaction will simply be removed from your records.
          </span>
        }
        visible={confirmDelete}
        // icon="pi pi-exclamation-triangle"
        className="confirm-delete-popup"
        accept={() => {
          deleteRequest();
        }}
        reject={() => {
          setConfirmDelete(false);
          setSelectedRecord();
        }}
        onHide={() => {
          setConfirmDelete(false);
          setSelectedRecord();
        }}
      />
      {Boolean(showRecordModal) && (
        <EditPaymentRecord
          show={showRecordModal}
          onHide={() => {
            setShowRecordModal(false);
          }}
          selectedRecord={selectedRecord}
          onSuccess={onCreateSuccess}
          isLeadSpecific={props.isLeadSpecific}
        />
      )}
      {Boolean(showRefundModal) && (
        <RefundPayment
          show={showRefundModal}
          onHide={() => setShowRefundModal(false)}
          selectedRecord={selectedRecord}
          onSuccess={onCreateSuccess}
          isLeadSpecific={props.isLeadSpecific}
        />
      )}
      <div className="">
        <div className="d-flex flex-wrap align-items-center filter-wrap mb-3 p-lg-0 p-2 w-100 align-items-center justify-content-between">
          <div className="p-2 filter-wraper">
            <span className="p-input-icon-right input-shadow w-100">
              <i className="icon-search F-size16"></i>
              <TextSnippetWrapper
                type="text"
                placeholder="Search"
                onChange={(e) => {
                  onChangeSearch(e);
                }}
                value={searchKey}
                onKeyDown={onSearchKeyDown}
                className="p-inputtext p-component input-search"
                autoComplete="off"
              />
            </span>
          </div>
          <div className="d-flex flex-wrap align-items-center p-2 filter-wraper gap-2">
            <label className="text-bold black-600  filter-label">Filters:</label>
            <div className="d-flex flex-column filter-wraper">
              <Calendar
                className="input-shadow filter-calender"
                id="icon"
                value={dateFilter}
                onChange={(e) => {
                  onChangeCalendar(e);
                }}
                selectionMode="range"
                showIcon
                placeholder="Date"
              />
            </div>
            {!isFromCaseSpecificModule && !props?.isLeadSpecific && (
              <div className="d-flex flex-column filter-wraper">
                <MultiSelect
                  value={selectedPractice}
                  options={practiceAreaOptions}
                  onChange={onChangePractice}
                  placeholder="Practice Area"
                  maxSelectedLabels={1}
                  display="chip"
                  filter
                  className="input-shadow w-100 F-size14 input-width"
                  panelFooterTemplate={isPracticeAreaLoading ? LoaderComponent : null}
                />
              </div>
            )}

            <div className="d-flex flex-column ">
              <div className="pointer ms-0">
                <i className="fas fa-sync-alt" onClick={refreshCase}></i>
                <Tooltip content="Reset Filter" position="top" target=".fa-sync-alt" showDelay={500} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="datatable-responsive bg-white shadow-middle mb-3">
        <ResizableDataTable
          value={paymentRecords.payment_records}
          className="p-datatable-responsive"
          paginator={false}
          storageKey="payment_history_column_widths"
          columns={[
            { field: 'date', header: 'Date', bodyClassName: 'ellipsis-text', body: dateBody },
            { field: 'case', header: 'Case', bodyClassName: 'ellipsis-text', body: caseBody, hidden: !isFromGlobalBilling },
            { field: 'related_to', header: 'Related To', bodyClassName: 'ellipsis-text', body: relatedToBody },
            { field: 'entered_by', header: 'Entered By', bodyClassName: 'ellipsis-text', body: enterByBody },
            { field: 'notes', header: 'Notes', bodyClassName: 'ellipsis-text', body: notesBody },
            { field: 'debit_account_name', header: 'Paid From', bodyClassName: 'ellipsis-text', body: paidFromBody },
            { field: 'deposit_account_name', header: 'Paid To', bodyClassName: 'ellipsis-text', body: paidToBody },
            { field: 'payment_method', header: 'Payment Method', bodyClassName: 'ellipsis-text', body: paymentMethodBody },
            { field: 'amount', header: 'Amount', bodyClassName: 'ellipsis-text', body: amountBody },
            {
              field: 'actions',
              header: '',
              bodyClassName: 'ellipsis-text',
              body: actionBody,
              sortable: false,
              fixed: true,
              width: '140px',
              resizable: false,
            },
          ]}
        />
        <div className="d-flex justify-content-start mt-3">
          <EllipsisPagination
            currentPage={currentPage}
            totalRecords={totalRecords}
            pageSize={pageLimit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    </div>
  );
};
